import { useState } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { authClient, signOut, useSession } from '@/lib/auth-client';
import { sessionOptions, projectsOptions } from '@/hooks/query-options';
import { Loader } from '@/components/ui/loader';
import { Button } from '@/components/ui/button';
import { LinkLogo } from '@/components/nav/link-logo';

export const Route = createFileRoute('/demo')({
  pendingComponent: () => <Loader fullScreen />,
  beforeLoad: async ({ context }) => {
    let session;
    try {
      session = await context.queryClient.ensureQueryData(sessionOptions());
    } catch {
      session = null;
    }
    const isAnon =
      (session?.user as { isAnonymous?: boolean } | undefined)?.isAnonymous ===
      true;

    // Anonymous visitor already in a session.
    if (session?.user && isAnon) {
      if (session.session?.activeOrganizationId) {
        // Enter only if the demo org is usable (has a project); a project-less
        // org would just bounce to onboarding, so treat it as unavailable.
        let hasProject = false;
        try {
          const projects =
            await context.queryClient.ensureQueryData(projectsOptions());
          hasProject = Array.isArray(projects) && projects.length > 0;
        } catch {
          hasProject = false;
        }
        if (hasProject) {
          throw redirect({ to: '/' });
        }
      }
      // No usable demo (never joined an org, or the org has no project): drop the
      // throwaway session so we don't strand them in onboarding, show unavailable.
      await signOut();
      return { view: 'unavailable' as const };
    }

    // Real signed-in user: confirm (the component handles a refused mint).
    if (session?.user) return { view: 'confirm' as const };

    // Logged-out visitor: the mint itself is the availability check. The server
    // refuses it when this env has no demo org.
    const { error } = await authClient.signIn.anonymous();
    if (error) return { view: 'unavailable' as const };
    window.location.href = '/';
    await new Promise(() => {}); // hold the render until the reload lands
    return { view: 'unavailable' as const };
  },
  component: DemoEntry,
});

function DemoEntry() {
  const { view } = Route.useRouteContext();
  const { data: session } = useSession();

  if (view === 'confirm') {
    return <ConfirmSwitch email={session?.user?.email} />;
  }
  return <DemoUnavailable />;
}

function ConfirmSwitch({ email }: { email?: string }) {
  const [unavailable, setUnavailable] = useState(false);

  // A session can only be active in one org and the demo runs in its own, so
  // sign out first, then mint the anonymous demo session and reload.
  const handleViewDemo = async () => {
    await signOut();
    const { error } = await authClient.signIn.anonymous();
    if (error) {
      setUnavailable(true);
      return;
    }
    window.location.href = '/';
  };

  if (unavailable) return <DemoUnavailable />;

  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-sm px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center">
          <LinkLogo />
          <h1 className="mt-12 text-3xl font-bold">View the Demo</h1>
          <p className="text-muted-foreground mt-4 font-medium">
            You&apos;re signed in{email ? ` as ${email}` : ''}. The demo runs in a
            separate demo account, so you&apos;ll be signed out of yours to view
            it.
          </p>
          <Button className="mt-8 w-full" onClick={handleViewDemo}>
            Sign Out &amp; View Demo
          </Button>
          <Button asChild variant="ghost" className="mt-3 w-full">
            <Link to="/">Back to My Dashboard</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

function DemoUnavailable() {
  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-sm px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center">
          <LinkLogo />
          <h1 className="mt-12 text-3xl font-bold">Demo Unavailable</h1>
          <p className="text-muted-foreground mt-4 font-medium">
            The demo isn&apos;t available right now. Please try again in a
            moment.
          </p>
          <Button asChild className="mt-8 w-full">
            <Link to="/sign-in">Back to Sign In</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
